import React, { FC, useContext, useEffect, useState, useCallback } from 'react'

import { useEditor } from '@craftjs/core'

import EditorContext from './EditorContext'
import useEditorActions from './useEditorActions'

import EmptyEditor from './editors/Empty'
import TextEditor from './editors/Text'
import ImageEditor from './editors/Image'
import VideoEditor from './editors/Video'
import FileEditor from './editors/File'
import HTMLEditor from './editors/HTML'
import TableEditor from './editors/Table'

import {
  ILayout,
  ILayout_Cell,
  ILayout_Cell_Type,
  ILayout_Editor,
} from '@kaidam/fc-shared/src/types/layout'

import {
  extractSetValue,
} from './utils'

const editors: Record<ILayout_Cell_Type, ILayout_Editor> = {
  empty: EmptyEditor,
  spacer: EmptyEditor,
  text: TextEditor,
  image: ImageEditor,
  video: VideoEditor,
  file: FileEditor,
  table: TableEditor,
  html: HTMLEditor,
  booking_details: EmptyEditor,
}

interface CellEditorProps {
  onChange: {
    (data: ILayout): void,
  },
}

const CellEditor: FC<React.PropsWithChildren<CellEditorProps>> = ({
  onChange,
}) => {

  // used to trigger the cell editor to open (whatever it likes)
  // on a double click
  const [ triggerEdit, setTriggerEdit ] = useState(false)
  const editorContext = useContext(EditorContext)
  
  const {
    exportLayout,
    deselect,
  } = useEditorActions()

  const {
    selectedId,
    props,
    actions,
  } = useEditor((state) => {
    const selectedId = extractSetValue(state.events.selected)
    let props = null
    let doubleClick = false
    if(selectedId) {
      const node = state.nodes[selectedId]
      node.data.custom
      if(node && node.data.props) {
        props = node.data.props
      }
    }
    return {
      selectedId,
      props,
      doubleClick,
    }
  })

  if(!props) return null

  const cell: ILayout_Cell = {
    type: props.type,
    isNew: props.isNew,
    config: props.config || {},
    data: props.data,
  }

  useEffect(() => {
    const event = editorContext.editorEvent
    if(!event) return
    if(event.id != selectedId) return
    if(event.action == 'edit') {
      setTriggerEdit(true)
    }
  }, [
    editorContext.editorEvent,
  ])

  // immediately switch off the trigger edit
  // if we see it (so child components)
  // only need to worry about opening their editor
  useEffect(() => {
    if(triggerEdit) setTriggerEdit(false)
  }, [
    triggerEdit,
  ])

  const onSave = useCallback(() => {
    onChange(exportLayout(editorContext.options))
  }, [
    editorContext.options,
  ])

  const Component = editors[cell.type]
  if(!Component) throw new Error(`no component type found for ${cell.type}`)

  return (
    <Component
      cell={ cell }
      options={ editorContext.options }
      triggerEdit={ triggerEdit }
      onSetEditing={ value => {
        editorContext.setSelectionLocked(value)
      }}
      onDeselect={ deselect }
      onChange={ data => {
        if(!selectedId) return
        actions.setProp(selectedId, props => {
          Object.keys(data).forEach(field => {
            props.data[field] = data[field]
          })
        })
      }}
      onSave={ onSave }
    />
  )

}

export default CellEditor